import { } from 'react-redux';

import { Action } from '@reduxjs/toolkit';

import { updateObject } from '../../utils';
import * as actionTypes from '../constants/actionTypes';

import { AppAction } from '../../../store';

import {
  DataLookup,
  DataLookupTag,
  LineOfBusiness,
  LobClass,
  OpportunityStage,
  ProspectStatus,
  QuoteStage,
  StatusOfAgencyModules,
  TruckingCompany,
  UserInfo
} from '../../models';

export type SharedState = {
  states: DataLookup | null;
  statesError: any | null;
  statesLoading: boolean;

  carriers: DataLookup | null;
  carriersError: any | null;
  carriersLoading: boolean;

  locations: DataLookup | null;
  locationsError: any | null;
  locationsLoading: boolean;

  lobClasses: Array<LobClass> | null;
  lobClassesError: any | null;
  lobClassesLoading: boolean;

  linesOfBusiness: Array<LineOfBusiness> | null;
  linesOfBusinessError: any | null;
  linesOfBusinessLoading: boolean;

  referralSources: DataLookup | null;
  referralSourcesError: any | null;
  referralSourcesLoading: boolean;

  tags: DataLookupTag | null;
  tagsError: any | null;
  tagsLoading: boolean;

  quoteStages: Array<QuoteStage> | null;
  quoteStagesError: any | null;
  quoteStagesLoading: boolean;

  mgas: DataLookup | null;
  mgasError: any | null;
  mgasLoading: boolean;

  opportunityStages: Array<OpportunityStage> | null;
  opportunityStagesError: any | null;
  opportunityStagesLoading: boolean;

  agencyName: string | null;
  agencyNameError: any | null;
  agencyNameLoading: boolean;

  policiesLookup: DataLookup | null;
  policiesLookupError: any | null;
  policiesLookupLoading: boolean;

  statuses: Array<ProspectStatus> | null;
  statusesError: any | null;
  statusesLoading: boolean;

  parentCarriers: Array<TruckingCompany> | null;
  parentCarriersError: any | null;
  parentCarriersLoading: boolean;

  maxFileUploadSize: number | null;
  maxFileUploadSizeError: any | null;
  maxFileUploadSizeLoading: boolean;

  statusOfAgencyModules: StatusOfAgencyModules | null;
  statusOfAgencyModulesError: any | null;
  statusOfAgencyModulesLoading: boolean;

  insureds: Array<TruckingCompany> | null;
  insuredsError: any | null;
  insuredsLoading: boolean;

  claims: DataLookup | null;
  claimsLoading: boolean;
  claimsError: any | null;

  insuredContactsLookup: DataLookup | null;
  insuredContactsLookupLoading: boolean;
  insuredContactsLookupError: any | null;

  insuredContactsOwnersLookup: DataLookup | null;
  insuredContactsOwnersLookupLoading: boolean;
  insuredContactsOwnersLookupError: any | null;

  driversLookup: DataLookup | null;
  driversLookupLoading: boolean;
  driversLookupError: any | null;

  vehiclesLookup: DataLookup | null;
  vehiclesLookupLoading: boolean;
  vehiclesLookupError: any | null;

  replacedTemplateMessage: string | null;
  replacedTemplateMessageError: any | null;
  replacedTemplateMessageLoading: boolean;

  motorTruckCargosLookup: DataLookup | null;
  motorTruckCargosLookupLoading: boolean;
  motorTruckCargosLookupError: any | null;

  pendingCancellationsLookup: DataLookup | null;
  pendingCancellationsLookupLoading: boolean;
  pendingCancellationsLookupError: any | null;

  truckingCompanyContactsLookup: DataLookup | null;
  truckingCompanyContactsLookupLoading: boolean;
  truckingCompanyContactsLookupError: any | null;

  workersCompensationsLookup: DataLookup | null;
  workersCompensationsLookupLoading: boolean;
  workersCompensationsLookupError: any | null;

  propertiesLookup: DataLookup | null;
  propertiesLookupLoading: boolean;
  propertiesLookupError: any | null;

  additionalInterestsLookup: DataLookup | null;
  additionalInterestsLookupLoading: boolean;
  additionalInterestsLookupError: any | null;

  generalLiabilitiesLookup: DataLookup | null;
  generalLiabilitiesLookupLoading: boolean;
  generalLiabilitiesLookupError: any | null;

  scheduleOfHazardsLookup: DataLookup | null;
  scheduleOfHazardsLookupLoading: boolean;
  scheduleOfHazardsLookupError: any | null;

  equipmentsLookup: DataLookup | null;
  equipmentsLookupLoading: boolean;
  equipmentsLookupError: any | null;

  agentsLookup: DataLookup | null;
  agentsLookupLoading: boolean;
  agentsLookupError: any | null;

  quotesLookup: DataLookup | null;
  quotesLookupLoading: boolean;
  quotesLookupError: any | null;

  signaturesLookup: DataLookup | null;
  signaturesLookupLoading: boolean;
  signaturesLookupError: any | null;

  boatsLookup: DataLookup | null;
  boatsLookupLoading: boolean;
  boatsLookupError: any | null;

  userInfo: UserInfo | null;
  userInfoLoading: boolean;
  userInfoError: any | null;

  formName: string | null;
  formNameLoading: boolean;
  formNameError: any | null;

  businessAutoAcord137sLookup: DataLookup | null;
  businessAutoAcord137sLookupLoading: boolean;
  businessAutoAcord137sLookupError: any | null;

  personalUmbrellasLookup: DataLookup | null;
  personalUmbrellasLookupLoading: boolean;
  personalUmbrellasLookupError: any | null;

  commercialUmbrellasLookup: DataLookup | null;
  commercialUmbrellasLookupLoading: boolean;
  commercialUmbrellasLookupError: any | null;
};

const initialState: SharedState = {
  states: null,
  statesError: null,
  statesLoading: false,

  carriers: null,
  carriersError: null,
  carriersLoading: false,

  locations: null,
  locationsError: null,
  locationsLoading: false,

  lobClasses: null,
  lobClassesError: null,
  lobClassesLoading: false,

  linesOfBusiness: null,
  linesOfBusinessError: null,
  linesOfBusinessLoading: false,

  referralSources: null,
  referralSourcesError: null,
  referralSourcesLoading: false,

  tags: null,
  tagsError: null,
  tagsLoading: false,

  quoteStages: null,
  quoteStagesError: null,
  quoteStagesLoading: false,

  mgas: null,
  mgasError: null,
  mgasLoading: false,

  opportunityStages: null,
  opportunityStagesError: null,
  opportunityStagesLoading: false,

  agencyName: null,
  agencyNameError: null,
  agencyNameLoading: true,

  policiesLookup: null,
  policiesLookupError: null,
  policiesLookupLoading: false,

  statuses: null,
  statusesError: null,
  statusesLoading: false,

  parentCarriers: null,
  parentCarriersError: null,
  parentCarriersLoading: false,

  maxFileUploadSize: null,
  maxFileUploadSizeError: null,
  maxFileUploadSizeLoading: false,

  statusOfAgencyModules: null,
  statusOfAgencyModulesError: null,
  statusOfAgencyModulesLoading: false,

  insureds: null,
  insuredsError: null,
  insuredsLoading: false,

  claims: null,
  claimsLoading: false,
  claimsError: null,

  insuredContactsLookup: null,
  insuredContactsLookupLoading: false,
  insuredContactsLookupError: null,

  insuredContactsOwnersLookup: null,
  insuredContactsOwnersLookupLoading: false,
  insuredContactsOwnersLookupError: null,

  driversLookup: null,
  driversLookupLoading: false,
  driversLookupError: null,

  vehiclesLookup: null,
  vehiclesLookupLoading: false,
  vehiclesLookupError: null,

  replacedTemplateMessage: null,
  replacedTemplateMessageError: null,
  replacedTemplateMessageLoading: false,

  motorTruckCargosLookup: null,
  motorTruckCargosLookupLoading: false,
  motorTruckCargosLookupError: null,

  pendingCancellationsLookup: null,
  pendingCancellationsLookupLoading: false,
  pendingCancellationsLookupError: null,

  truckingCompanyContactsLookup: null,
  truckingCompanyContactsLookupLoading: false,
  truckingCompanyContactsLookupError: null,

  workersCompensationsLookup: null,
  workersCompensationsLookupLoading: false,
  workersCompensationsLookupError: null,

  propertiesLookup: null,
  propertiesLookupLoading: false,
  propertiesLookupError: null,

  additionalInterestsLookup: null,
  additionalInterestsLookupLoading: false,
  additionalInterestsLookupError: null,

  generalLiabilitiesLookup: null,
  generalLiabilitiesLookupLoading: false,
  generalLiabilitiesLookupError: null,

  scheduleOfHazardsLookup: null,
  scheduleOfHazardsLookupLoading: false,
  scheduleOfHazardsLookupError: null,

  equipmentsLookup: null,
  equipmentsLookupLoading: false,
  equipmentsLookupError: null,

  agentsLookup: null,
  agentsLookupLoading: false,
  agentsLookupError: null,

  quotesLookup: null,
  quotesLookupLoading: false,
  quotesLookupError: null,

  signaturesLookup: null,
  signaturesLookupLoading: false,
  signaturesLookupError: null,

  boatsLookup: null,
  boatsLookupLoading: false,
  boatsLookupError: null,

  userInfo: null,
  userInfoLoading: false,
  userInfoError: null,

  formName: null,
  formNameLoading: false,
  formNameError: null,

  businessAutoAcord137sLookup: null,
  businessAutoAcord137sLookupLoading: false,
  businessAutoAcord137sLookupError: null,

  personalUmbrellasLookup: null,
  personalUmbrellasLookupLoading: false,
  personalUmbrellasLookupError: null,

  commercialUmbrellasLookup: null,
  commercialUmbrellasLookupLoading: false,
  commercialUmbrellasLookupError: null,
};

const getStatesStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    statesError: null,
    statesLoading: true,
  });
};

const getStatesSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    states: action.data,
    statesError: null,
    statesLoading: false,
  });
};

const getStatesFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    states: null,
    statesError: action.error,
    statesLoading: false,
  });
};

const getCarriersStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    carriersError: null,
    carriersLoading: true,
  });
};

const getCarriersSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    carriers: action.data,
    carriersError: null,
    carriersLoading: false,
  });
};

const getCarriersFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    carriers: null,
    carriersError: action.error,
    carriersLoading: false,
  });
};

const getLocationsStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    locationsError: null,
    locationsLoading: true,
  });
};

const getLocationsSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    locations: action.data,
    locationsError: null,
    locationsLoading: false,
  });
};

const getLocationsFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    locations: null,
    locationsError: action.error,
    locationsLoading: false,
  });
};

const getLOBClassesStart = (state: SharedState, action: AppAction<Array<LobClass>>) => {
  return updateObject(state, {
    lobClassesError: null,
    lobClassesLoading: true,
  });
};

const getLOBClassesSuccess = (state: SharedState, action: AppAction<Array<LobClass>>) => {
  return updateObject(state, {
    lobClasses: action.data,
    lobClassesError: null,
    lobClassesLoading: false,
  });
};

const getLOBClassesFail = (state: SharedState, action: AppAction<Array<LobClass>>) => {
  return updateObject(state, {
    lobClasses: null,
    lobClassesError: action.error,
    lobClassesLoading: false,
  });
};

const getLinesOfBusinessStart = (state: SharedState, action: AppAction<Array<LineOfBusiness>>) => {
  return updateObject(state, {
    linesOfBusinessError: null,
    linesOfBusinessLoading: true,
  });
};

const getLinesOfBusinessSuccess = (state: SharedState, action: AppAction<Array<LineOfBusiness>>) => {
  return updateObject(state, {
    linesOfBusiness: action.data,
    linesOfBusinessError: null,
    linesOfBusinessLoading: false,
  });
};

const getLinesOfBusinessFail = (state: SharedState, action: AppAction<Array<LineOfBusiness>>) => {
  return updateObject(state, {
    linesOfBusiness: null,
    linesOfBusinessError: action.error,
    linesOfBusinessLoading: false,
  });
};

const getReferralSourcesStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    referralSourcesError: null,
    referralSourcesLoading: true,
  });
};

const getReferralSourcesSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    referralSources: action.data,
    referralSourcesError: null,
    referralSourcesLoading: false,
  });
};

const getReferralSourcesFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    referralSources: null,
    referralSourcesError: action.error,
    referralSourcesLoading: false,
  });
};

const getTagsStart = (state: SharedState, action: AppAction<DataLookupTag>) => {
  return updateObject(state, {
    tagsError: null,
    tagsLoading: true,
  });
};

const getTagsSuccess = (state: SharedState, action: AppAction<DataLookupTag>) => {
  return updateObject(state, {
    tags: action.data,
    tagsError: null,
    tagsLoading: false,
  });
};

const getTagsFail = (state: SharedState, action: AppAction<DataLookupTag>) => {
  return updateObject(state, {
    tags: null,
    tagsError: action.error,
    tagsLoading: false,
  });
};

const getQuoteStagesStart = (state: SharedState, action: AppAction<Array<QuoteStage>>) => {
  return updateObject(state, {
    quoteStagesError: null,
    quoteStagesLoading: true,
  });
};

const getQuoteStagesSuccess = (state: SharedState, action: AppAction<Array<QuoteStage>>) => {
  return updateObject(state, {
    quoteStages: action.data,
    quoteStagesError: null,
    quoteStagesLoading: false,
  });
};

const getQuoteStagesFail = (state: SharedState, action: AppAction<Array<QuoteStage>>) => {
  return updateObject(state, {
    quoteStages: null,
    quoteStagesError: action.error,
    quoteStagesLoading: false,
  });
};

const getMgasStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    mgasError: null,
    mgasLoading: true,
  });
};

const getMgasSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    mgas: action.data,
    mgasError: null,
    mgasLoading: false,
  });
};

const getMgasFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    mgas: null,
    mgasError: action.error,
    mgasLoading: false,
  });
};

const getOpportunityStagesStart = (state: SharedState, action: AppAction<Array<OpportunityStage>>) => {
  return updateObject(state, {
    opportunityStagesError: null,
    opportunityStagesLoading: true,
  });
};

const getOpportunityStagesSuccess = (state: SharedState, action: AppAction<Array<OpportunityStage>>) => {
  return updateObject(state, {
    opportunityStages: action.data,
    opportunityStagesError: null,
    opportunityStagesLoading: false,
  });
};

const getOpportunityStagesFail = (state: SharedState, action: AppAction<Array<OpportunityStage>>) => {
  return updateObject(state, {
    opportunityStages: null,
    opportunityStagesError: action.error,
    opportunityStagesLoading: false,
  });
};

const getAgencyNameStart = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    agencyNameError: null,
    agencyNameLoading: true,
  });
};

const getAgencyNameSuccess = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    agencyName: action.data,
    agencyNameError: null,
    agencyNameLoading: false,
  });
};

const getAgencyNameFail = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    agencyName: null,
    agencyNameError: action.error,
    agencyNameLoading: false,
  });
};

const getPoliciesLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    policiesLookupError: null,
    policiesLookupLoading: true,
  });
};

const getPoliciesLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    policiesLookup: action.data,
    policiesLookupError: null,
    policiesLookupLoading: false,
  });
};

const getPoliciesLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    policiesLookup: null,
    policiesLookupError: action.data,
    policiesLookupLoading: false,
  });
};

const getProspectStatusesStart = (state: SharedState, action: AppAction<Array<ProspectStatus>>) => {
  return updateObject(state, {
    statusesError: null,
    statusesLoading: true,
  });
};

const getProspectStatusesSuccess = (state: SharedState, action: AppAction<Array<ProspectStatus>>) => {
  return updateObject(state, {
    statuses: action.data,
    statusesError: null,
    statusesLoading: false,
  });
};

const getProspectStatusesFail = (state: SharedState, action: AppAction<Array<ProspectStatus>>) => {
  return updateObject(state, {
    statuses: null,
    statusesError: action.error,
    statusesLoading: false,
  });
};

const getParentCarriersStart = (state: SharedState, action: AppAction<Array<TruckingCompany>>) => {
  return updateObject(state, {
    parentCarriersError: null,
    parentCarriersLoading: true,
  });
};

const getParentCarriersSuccess = (state: SharedState, action: AppAction<Array<TruckingCompany>>) => {
  return updateObject(state, {
    parentCarriers: action.data,
    parentCarriersError: null,
    parentCarriersLoading: false,
  });
};

const getParentCarriersFail = (state: SharedState, action: AppAction<Array<TruckingCompany>>) => {
  return updateObject(state, {
    parentCarriers: null,
    parentCarriersError: action.error,
    parentCarriersLoading: false,
  });
};

const getMaxFileUploadSizeStart = (state: SharedState, action: AppAction<number>) => {
  return updateObject(state, {
    maxFileUploadSize: null,
    maxFileUploadSizeError: null,
    maxFileUploadSizeLoading: true,
  });
};

const getMaxFileUploadSizeSuccess = (state: SharedState, action: AppAction<number>) => {
  return updateObject(state, {
    maxFileUploadSize: action.data,
    maxFileUploadSizeError: null,
    maxFileUploadSizeLoading: false,
  });
};

const getMaxFileUploadSizeFail = (state: SharedState, action: AppAction<number>) => {
  return updateObject(state, {
    maxFileUploadSize: null,
    maxFileUploadSizeError: action.error,
    maxFileUploadSizeLoading: false,
  });
};

const getStatusOfAgencyModulesStart = (state: SharedState, action: AppAction<StatusOfAgencyModules>) => {
  return updateObject(state, {
    statusOfAgencyModules: null,
    statusOfAgencyModulesError: null,
    statusOfAgencyModulesLoading: true,
  });
};

const getStatusOfAgencyModulesSuccess = (state: SharedState, action: AppAction<StatusOfAgencyModules>) => {
  return updateObject(state, {
    statusOfAgencyModules: action.data,
    statusOfAgencyModulesError: null,
    statusOfAgencyModulesLoading: false,
  });
};

const getStatusOfAgencyModulesFail = (state: SharedState, action: AppAction<StatusOfAgencyModules>) => {
  return updateObject(state, {
    statusOfAgencyModules: null,
    statusOfAgencyModulesError: action.error,
    statusOfAgencyModulesLoading: false,
  });
};

const getInsuredsStart = (state: SharedState, action: AppAction<Array<TruckingCompany>>) => {
  return updateObject(state, {
    insureds: null,
    insuredsError: null,
    insuredsLoading: true,
  });
};

const getInsuredsSuccess = (state: SharedState, action: AppAction<Array<TruckingCompany>>) => {
  return updateObject(state, {
    insureds: action.data,
    insuredsError: null,
    insuredsLoading: false,
  });
};

const getInsuredsFail = (state: SharedState, action: AppAction<Array<TruckingCompany>>) => {
  return updateObject(state, {
    insureds: null,
    insuredsError: action.error,
    insuredsLoading: false,
  });
};

const getClaimsStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    claimsError: null,
    claimsLoading: true,
  });
};

const getClaimsSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    claims: action.data,
    claimsError: null,
    claimsLoading: false,
  });
};

const getClaimsFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    claims: null,
    claimsError: action.error,
    claimsLoading: false,
  });
};

const getInsuredContactsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    insuredContactsLookupError: null,
    insuredContactsLookupLoading: true,
  });
};

const getInsuredContactsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    insuredContactsLookup: action.data,
    insuredContactsLookupError: null,
    insuredContactsLookupLoading: false,
  });
};

const getInsuredContactsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    insuredContactsLookup: null,
    insuredContactsLookupError: action.error,
    insuredContactsLookupLoading: false,
  });
};

const getInsuredContactsOwnersLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    insuredContactsOwnersLookupError: null,
    insuredContactsOwnersLookupLoading: true,
  });
};

const getInsuredContactsOwnersLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    insuredContactsOwnersLookup: action.data,
    insuredContactsOwnersLookupError: null,
    insuredContactsOwnersLookupLoading: false,
  });
};

const getInsuredContactsOwnersLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    insuredContactsOwnersLookup: null,
    insuredContactsOwnersLookupError: action.error,
    insuredContactsOwnersLookupLoading: false,
  });
};

const getDriversLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    driversLookupError: null,
    driversLookupLoading: true,
  });
};

const getDriversLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    driversLookup: action.data,
    driversLookupError: null,
    driversLookupLoading: false,
  });
};

const getDriversLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    driversLookup: null,
    driversLookupError: action.error,
    driversLookupLoading: false,
  });
};

const getVehiclesLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    vehiclesLookupError: null,
    vehiclesLookupLoading: true,
  });
};

const getVehiclesLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    vehiclesLookup: action.data,
    vehiclesLookupError: null,
    vehiclesLookupLoading: false,
  });
};

const getVehiclesLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    vehiclesLookup: null,
    vehiclesLookupError: action.error,
    vehiclesLookupLoading: false,
  });
};

const replaceMessagePlaceholdersStart = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    replacedTemplateMessage: null,
    replacedTemplateMessageError: null,
    replacedTemplateMessageLoading: true,
  });
};

const replaceMessagePlaceholdersSuccess = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    replacedTemplateMessage: action.data,
    replacedTemplateMessageError: null,
    replacedTemplateMessageLoading: false,
  });
};

const replaceMessagePlaceholdersFail = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    replacedTemplateMessage: null,
    replacedTemplateMessageError: action.error,
    replacedTemplateMessageLoading: false,
  });
};

const getMotorTruckCargosLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    motorTruckCargosLookupError: null,
    motorTruckCargosLookupLoading: true,
  });
};

const getMotorTruckCargosLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    motorTruckCargosLookup: action.data,
    motorTruckCargosLookupError: null,
    motorTruckCargosLookupLoading: false,
  });
};

const getMotorTruckCargosLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    motorTruckCargosLookup: null,
    motorTruckCargosLookupError: action.error,
    motorTruckCargosLookupLoading: false,
  });
};

const getPendingCancellationsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    pendingCancellationsLookupError: null,
    pendingCancellationsLookupLoading: true,
  });
};

const getPendingCancellationsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    pendingCancellationsLookup: action.data,
    pendingCancellationsLookupError: null,
    pendingCancellationsLookupLoading: false,
  });
};

const getPendingCancellationsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    pendingCancellationsLookup: null,
    pendingCancellationsLookupError: action.error,
    pendingCancellationsLookupLoading: false,
  });
};

const getTruckingCompanyContactsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    truckingCompanyContactsLookupError: null,
    truckingCompanyContactsLookupLoading: true,
  });
};

const getTruckingCompanyContactsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {  
  return updateObject(state, {
    truckingCompanyContactsLookup: action.data,
    truckingCompanyContactsLookupError: null,
    truckingCompanyContactsLookupLoading: false,
  });
};

const getTruckingCompanyContactsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    truckingCompanyContactsLookup: null,
    truckingCompanyContactsLookupError: action.error,
    truckingCompanyContactsLookupLoading: false,
  });
};

const getWorkersCompensationsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    workersCompensationsLookupError: null,
    workersCompensationsLookupLoading: true,
  });
};

const getWorkersCompensationsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    workersCompensationsLookup: action.data,
    workersCompensationsLookupError: null,
    workersCompensationsLookupLoading: false,
  });
};

const getWorkersCompensationsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    workersCompensationsLookup: null,
    workersCompensationsLookupError: action.error,
    workersCompensationsLookupLoading: false,
  });
};

const getPropertiesLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    propertiesLookupError: null,
    propertiesLookupLoading: true,
  });
};

const getPropertiesLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    propertiesLookup: action.data,
    propertiesLookupError: null,
    propertiesLookupLoading: false,
  });
};

const getPropertiesLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    propertiesLookup: null,
    propertiesLookupError: action.error,
    propertiesLookupLoading: false,
  });
};

const getAdditionalInterestsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    additionalInterestsLookupError: null,
    additionalInterestsLookupLoading: true,
  });
};

const getAdditionalInterestsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    additionalInterestsLookup: action.data,
    additionalInterestsLookupError: null,
    additionalInterestsLookupLoading: false,
  });
};

const getAdditionalInterestsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    additionalInterestsLookup: null,
    additionalInterestsLookupError: action.error,
    additionalInterestsLookupLoading: false,
  });
};

const getGeneralLiabilitiesLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    generalLiabilitiesLookupError: null,
    generalLiabilitiesLookupLoading: true,
  });
};

const getGeneralLiabilitiesLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    generalLiabilitiesLookup: action.data,
    generalLiabilitiesLookupError: null,
    generalLiabilitiesLookupLoading: false,
  });
};

const getGeneralLiabilitiesLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    generalLiabilitiesLookup: null,
    generalLiabilitiesLookupError: action.error,
    generalLiabilitiesLookupLoading: false,
  });
};

const getScheduleOfHazardsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    scheduleOfHazardsLookupError: null,
    scheduleOfHazardsLookupLoading: true,
  });
};

const getScheduleOfHazardsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    scheduleOfHazardsLookup: action.data,
    scheduleOfHazardsLookupError: null,
    scheduleOfHazardsLookupLoading: false,
  });
};

const getScheduleOfHazardsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    scheduleOfHazardsLookup: null,
    scheduleOfHazardsLookupError: action.error,
    scheduleOfHazardsLookupLoading: false,
  });
};

const getEquipmentsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    equipmentsLookupError: null,
    equipmentsLookupLoading: true,
  });
};

const getEquipmentsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    equipmentsLookup: action.data,
    equipmentsLookupError: null,
    equipmentsLookupLoading: false,
  });
};

const getEquipmentsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    equipmentsLookup: null,
    equipmentsLookupError: action.error,
    equipmentsLookupLoading: false,
  });
};

const getAgentsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    agentsLookupError: null,
    agentsLookupLoading: true,
  });
};

const getAgentsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    agentsLookup: action.data,
    agentsLookupError: null,
    agentsLookupLoading: false,
  });
};

const getAgentsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    agentsLookup: null,
    agentsLookupError: action.error,
    agentsLookupLoading: false,
  });
};

const getQuotesLookupStart = (state: SharedState, action: AppAction<DataLookup>) => { 
  return updateObject(state, {
    quotesLookupError: null,
    quotesLookupLoading: true,
  });
};

const getQuotesLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    quotesLookup: action.data,
    quotesLookupError: null,
    quotesLookupLoading: false,
  });
};

const getQuotesLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    quotesLookup: null,
    quotesLookupError: action.error,
    quotesLookupLoading: false,
  });
};

const getSignaturesLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    signaturesLookupError: null,
    signaturesLookupLoading: true,
  });
};

const getSignaturesLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    signaturesLookup: action.data,
    signaturesLookupError: null,
    signaturesLookupLoading: false,
  });
};

const getSignaturesLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    signaturesLookup: null,
    signaturesLookupError: action.error,
    signaturesLookupLoading: false,
  });
};

const getBoatsLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    boatsLookupError: null,
    boatsLookupLoading: true,
  });
};

const getBoatsLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    boatsLookup: action.data,
    boatsLookupError: null,
    boatsLookupLoading: false,
  });
};

const getBoatsLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    boatsLookup: null,
    boatsLookupError: action.error,
    boatsLookupLoading: false,
  });
};

const getUserInfoStart = (state: SharedState, action: AppAction<UserInfo>) => {
  return updateObject(state, {
    userInfo: null,
    userInfoError: null,
    userInfoLoading: true,
  });
};

const getUserInfoSuccess = (state: SharedState, action: AppAction<UserInfo>) => {
  return updateObject(state, {
    userInfo: action.data,
    userInfoError: null,
    userInfoLoading: false,
  });
};

const getUserInfoFail = (state: SharedState, action: AppAction<UserInfo>) => {
  return updateObject(state, {
    userInfo: null,
    userInfoError: action.error,
    userInfoLoading: false,
  });
};

const getFormNameStart = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    formName: null,
    formNameError: null,
    formNameLoading: true,
  });
};

const getFormNameSuccess = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    formName: action.data,
    formNameError: null,
    formNameLoading: false,
  });
};

const getFormNameFail = (state: SharedState, action: AppAction<string>) => {
  return updateObject(state, {
    formName: null,
    formNameError: action.error,
    formNameLoading: false,
  });
};

const getBusinessAutoAcord137sLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    businessAutoAcord137sLookup: null,
    businessAutoAcord137sLookupError: null,
    businessAutoAcord137sLookupLoading: true,
  });
};

const getBusinessAutoAcord137sLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    businessAutoAcord137sLookup: action.data,
    businessAutoAcord137sLookupError: null,
    businessAutoAcord137sLookupLoading: false,
  });
};

const getBusinessAutoAcord137sLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    businessAutoAcord137sLookup: null,
    businessAutoAcord137sLookupError: action.error,
    businessAutoAcord137sLookupLoading: false,
  });
};

const getPersonalUmbrellasLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    personalUmbrellasLookup: null,
    personalUmbrellasLookupError: null,
    personalUmbrellasLookupLoading: true,
  });
};

const getPersonalUmbrellasLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    personalUmbrellasLookup: action.data,
    personalUmbrellasLookupError: null,
    personalUmbrellasLookupLoading: false,
  });
};

const getPersonalUmbrellasLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    personalUmbrellasLookup: null,
    personalUmbrellasLookupError: action.error,
    personalUmbrellasLookupLoading: false,
  });
};

const getCommercialUmbrellasLookupStart = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    commercialUmbrellasLookup: null,
    commercialUmbrellasLookupError: null,
    commercialUmbrellasLookupLoading: true,
  });
};

const getCommercialUmbrellasLookupSuccess = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    commercialUmbrellasLookup: action.data,
    commercialUmbrellasLookupError: null,
    commercialUmbrellasLookupLoading: false,
  });
};

const getCommercialUmbrellasLookupFail = (state: SharedState, action: AppAction<DataLookup>) => {
  return updateObject(state, {
    commercialUmbrellasLookup: null,
    commercialUmbrellasLookupError: action.error,
    commercialUmbrellasLookupLoading: false,
  });
};

const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case actionTypes.GET_STATES_START:
      return getStatesStart(state, action as AppAction<DataLookup>);
    case actionTypes.GET_STATES_SUCCESS:
      return getStatesSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.GET_STATES_FAIL:
      return getStatesFail(state, action as AppAction<DataLookup>);

    case actionTypes.GET_CARRIERS_START:
      return getCarriersStart(state, action as AppAction<DataLookup>);
    case actionTypes.GET_CARRIERS_SUCCESS:
      return getCarriersSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.GET_CARRIERS_FAIL:
      return getCarriersFail(state, action as AppAction<DataLookup>);

    case actionTypes.GET_LOCATIONS_START:
      return getLocationsStart(state, action as AppAction<DataLookup>);
    case actionTypes.GET_LOCATIONS_SUCCESS:
      return getLocationsSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.GET_LOCATIONS_FAIL:
      return getLocationsFail(state, action as AppAction<DataLookup>);

    case actionTypes.GET_LOB_CLASSES_START:
      return getLOBClassesStart(state, action as AppAction<Array<LobClass>>);
    case actionTypes.GET_LOB_CLASSES_SUCCESS:
      return getLOBClassesSuccess(state, action as AppAction<Array<LobClass>>);
    case actionTypes.GET_LOB_CLASSES_FAIL:
      return getLOBClassesFail(state, action as AppAction<Array<LobClass>>);

    case actionTypes.GET_LINES_OF_BUSINESS_START:
      return getLinesOfBusinessStart(state, action as AppAction<Array<LineOfBusiness>>);
    case actionTypes.GET_LINES_OF_BUSINESS_SUCCESS:
      return getLinesOfBusinessSuccess(state, action as AppAction<Array<LineOfBusiness>>);
    case actionTypes.GET_LINES_OF_BUSINESS_FAIL:
      return getLinesOfBusinessFail(state, action as AppAction<Array<LineOfBusiness>>);

    case actionTypes.GET_REFERRAL_SOURCES_START:
      return getReferralSourcesStart(state, action as AppAction<DataLookup>);
    case actionTypes.GET_REFERRAL_SOURCES_SUCCESS:
      return getReferralSourcesSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.GET_REFERRAL_SOURCES_FAIL:
      return getReferralSourcesFail(state, action as AppAction<DataLookup>);

    case actionTypes.GET_TAGS_START:
      return getTagsStart(state, action as AppAction<DataLookupTag>);
    case actionTypes.GET_TAGS_SUCCESS:
      return getTagsSuccess(state, action as AppAction<DataLookupTag>);
    case actionTypes.GET_TAGS_FAIL:
      return getTagsFail(state, action as AppAction<DataLookupTag>);

    case actionTypes.GET_QUOTE_STAGES_START:
      return getQuoteStagesStart(state, action as AppAction<Array<QuoteStage>>);
    case actionTypes.GET_QUOTE_STAGES_SUCCESS:
      return getQuoteStagesSuccess(state, action as AppAction<Array<QuoteStage>>);
    case actionTypes.GET_QUOTE_STAGES_FAIL:
      return getQuoteStagesFail(state, action as AppAction<Array<QuoteStage>>);

    case actionTypes.GET_MGAS_START:
      return getMgasStart(state, action as AppAction<DataLookup>);
    case actionTypes.GET_MGAS_SUCCESS:
      return getMgasSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.GET_MGAS_FAIL:
      return getMgasFail(state, action as AppAction<DataLookup>);

    case actionTypes.GET_OPPORTUNITY_STAGES_START:
      return getOpportunityStagesStart(state, action as AppAction<Array<OpportunityStage>>);
    case actionTypes.GET_OPPORTUNITY_STAGES_SUCCESS:
      return getOpportunityStagesSuccess(state, action as AppAction<Array<OpportunityStage>>);
    case actionTypes.GET_OPPORTUNITY_STAGES_FAIL:
      return getOpportunityStagesFail(state, action as AppAction<Array<OpportunityStage>>);

    case actionTypes.GET_AGENCY_NAME_START:
      return getAgencyNameStart(state, action as AppAction<string>);
    case actionTypes.GET_AGENCY_NAME_SUCCESS:
      return getAgencyNameSuccess(state, action as AppAction<string>);
    case actionTypes.GET_AGENCY_NAME_FAIL:
      return getAgencyNameFail(state, action as AppAction<string>);

    case actionTypes.SHARED_GET_POLICIES_LOOKUP_START:
      return getPoliciesLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_POLICIES_LOOKUP_SUCCESS:
      return getPoliciesLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_POLICIES_LOOKUP_FAIL:
      return getPoliciesLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.GET_PROSPECT_STATUSES_START:
      return getProspectStatusesStart(state, action as AppAction<Array<ProspectStatus>>);
    case actionTypes.GET_PROSPECT_STATUSES_SUCCESS:
      return getProspectStatusesSuccess(state, action as AppAction<Array<ProspectStatus>>);
    case actionTypes.GET_PROSPECT_STATUSES_FAIL:
      return getProspectStatusesFail(state, action as AppAction<Array<ProspectStatus>>);

    case actionTypes.GET_PARENT_CARRIERS_START:
      return getParentCarriersStart(state, action as AppAction<Array<TruckingCompany>>);
    case actionTypes.GET_PARENT_CARRIERS_SUCCESS:
      return getParentCarriersSuccess(state, action as AppAction<Array<TruckingCompany>>);
    case actionTypes.GET_PARENT_CARRIERS_FAIL:
      return getParentCarriersFail(state, action as AppAction<Array<TruckingCompany>>);

    case actionTypes.GET_MAX_FILE_UPLOAD_SIZE_START:
      return getMaxFileUploadSizeStart(state, action as AppAction<number>);
    case actionTypes.GET_MAX_FILE_UPLOAD_SIZE_SUCCESS:
      return getMaxFileUploadSizeSuccess(state, action as AppAction<number>);
    case actionTypes.GET_MAX_FILE_UPLOAD_SIZE_FAIL:
      return getMaxFileUploadSizeFail(state, action as AppAction<number>);

    case actionTypes.GET_STATUS_OF_AGENCY_MODULES_START:
      return getStatusOfAgencyModulesStart(state, action as AppAction<StatusOfAgencyModules>);
    case actionTypes.GET_STATUS_OF_AGENCY_MODULES_SUCCESS:
      return getStatusOfAgencyModulesSuccess(state, action as AppAction<StatusOfAgencyModules>);
    case actionTypes.GET_STATUS_OF_AGENCY_MODULES_FAIL:
      return getStatusOfAgencyModulesFail(state, action as AppAction<StatusOfAgencyModules>);

    case actionTypes.SHARED_GET_INSUREDS_BY_AGENCY_START:
      return getInsuredsStart(state, action as AppAction<Array<TruckingCompany>>);
    case actionTypes.SHARED_GET_INSUREDS_BY_AGENCY_SUCCESS:
      return getInsuredsSuccess(state, action as AppAction<Array<TruckingCompany>>);
    case actionTypes.SHARED_GET_INSUREDS_BY_AGENCY_FAIL:
      return getInsuredsFail(state, action as AppAction<Array<TruckingCompany>>);

    case actionTypes.SHARED_GET_CLAIMS_START:
      return getClaimsStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_CLAIMS_SUCCESS:
      return getClaimsSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_CLAIMS_FAIL:
      return getClaimsFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_INSURED_CONTACTS_LOOKUP_START:
      return getInsuredContactsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_INSURED_CONTACTS_LOOKUP_SUCCESS:
      return getInsuredContactsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_INSURED_CONTACTS_LOOKUP_FAIL:
      return getInsuredContactsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_INSURED_CONTACTS_OWNERS_LOOKUP_START:
      return getInsuredContactsOwnersLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_INSURED_CONTACTS_OWNERS_LOOKUP_SUCCESS:
      return getInsuredContactsOwnersLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_INSURED_CONTACTS_OWNERS_LOOKUP_FAIL:
      return getInsuredContactsOwnersLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_DRIVERS_LOOKUP_START:
      return getDriversLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_DRIVERS_LOOKUP_SUCCESS:
      return getDriversLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_DRIVERS_LOOKUP_FAIL:
      return getDriversLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_VEHICLES_LOOKUP_START:
      return getVehiclesLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_VEHICLES_LOOKUP_SUCCESS:
      return getVehiclesLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_VEHICLES_LOOKUP_FAIL:
      return getVehiclesLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.REPLACE_MESSAGE_PLACEHOLDERS_START:
      return replaceMessagePlaceholdersStart(state, action as AppAction<string>);
    case actionTypes.REPLACE_MESSAGE_PLACEHOLDERS_SUCCESS:
      return replaceMessagePlaceholdersSuccess(state, action as AppAction<string>);
    case actionTypes.REPLACE_MESSAGE_PLACEHOLDERS_FAIL:
      return replaceMessagePlaceholdersFail(state, action as AppAction<string>);

    case actionTypes.SHARED_GET_MOTOR_TRUCK_CARGOS_LOOKUP_START:
      return getMotorTruckCargosLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_MOTOR_TRUCK_CARGOS_LOOKUP_SUCCESS:
      return getMotorTruckCargosLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_MOTOR_TRUCK_CARGOS_LOOKUP_FAIL:
      return getMotorTruckCargosLookupFail(state, action as AppAction<DataLookup>);


    case actionTypes.SHARED_GET_PENDING_CANCELLATIONS_LOOKUP_START:
      return getPendingCancellationsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_PENDING_CANCELLATIONS_LOOKUP_SUCCESS:
      return getPendingCancellationsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_PENDING_CANCELLATIONS_LOOKUP_FAIL:
      return getPendingCancellationsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_TRUCKING_COMPANY_CONTACTS_LOOKUP_START:
      return getTruckingCompanyContactsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_TRUCKING_COMPANY_CONTACTS_LOOKUP_SUCCESS:
      return getTruckingCompanyContactsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_TRUCKING_COMPANY_CONTACTS_LOOKUP_FAIL:
      return getTruckingCompanyContactsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_WORKERS_COMPENSATIONS_LOOKUP_START:
      return getWorkersCompensationsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_WORKERS_COMPENSATIONS_LOOKUP_SUCCESS:
      return getWorkersCompensationsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_WORKERS_COMPENSATIONS_LOOKUP_FAIL:
      return getWorkersCompensationsLookupFail(state, action as AppAction<DataLookup>);


    case actionTypes.SHARED_GET_PROPERTIES_LOOKUP_START:
      return getPropertiesLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_PROPERTIES_LOOKUP_SUCCESS:
      return getPropertiesLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_PROPERTIES_LOOKUP_FAIL:
      return getPropertiesLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_ADDITIONAL_INTERESTS_LOOKUP_START:
      return getAdditionalInterestsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_ADDITIONAL_INTERESTS_LOOKUP_SUCCESS:
      return getAdditionalInterestsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_ADDITIONAL_INTERESTS_LOOKUP_FAIL:
      return getAdditionalInterestsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_GENERAL_LIABILITIES_LOOKUP_START:
      return getGeneralLiabilitiesLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_GENERAL_LIABILITIES_LOOKUP_SUCCESS:
      return getGeneralLiabilitiesLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_GENERAL_LIABILITIES_LOOKUP_FAIL:
      return getGeneralLiabilitiesLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_SCHEDULE_OF_HAZARDS_LOOKUP_START:
      return getScheduleOfHazardsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_SCHEDULE_OF_HAZARDS_LOOKUP_SUCCESS:
      return getScheduleOfHazardsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_SCHEDULE_OF_HAZARDS_LOOKUP_FAIL:
      return getScheduleOfHazardsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_EQUIPMENTS_LOOKUP_START:
      return getEquipmentsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_EQUIPMENTS_LOOKUP_SUCCESS:
      return getEquipmentsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_EQUIPMENTS_LOOKUP_FAIL:
      return getEquipmentsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_AGENTS_LOOKUP_START:
      return getAgentsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_AGENTS_LOOKUP_SUCCESS:
      return getAgentsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_AGENTS_LOOKUP_FAIL:
      return getAgentsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_QUOTES_LOOKUP_START:
      return getQuotesLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_QUOTES_LOOKUP_SUCCESS:
      return getQuotesLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_QUOTES_LOOKUP_FAIL:
      return getQuotesLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_SIGNATURES_LOOKUP_START:
      return getSignaturesLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_SIGNATURES_LOOKUP_SUCCESS:
      return getSignaturesLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_SIGNATURES_LOOKUP_FAIL:
      return getSignaturesLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_BOATS_LOOKUP_START:
      return getBoatsLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_BOATS_LOOKUP_SUCCESS:
      return getBoatsLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_BOATS_LOOKUP_FAIL:
      return getBoatsLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_USER_INFO_START:
      return getUserInfoStart(state, action as AppAction<UserInfo>);
    case actionTypes.SHARED_GET_USER_INFO_SUCCESS:
      return getUserInfoSuccess(state, action as AppAction<UserInfo>);
    case actionTypes.SHARED_GET_USER_INFO_FAIL:
      return getUserInfoFail(state, action as AppAction<UserInfo>);

    case actionTypes.SHARED_GET_FORM_NAME_START:
      return getFormNameStart(state, action as AppAction<string>);
    case actionTypes.SHARED_GET_FORM_NAME_SUCCESS:
      return getFormNameSuccess(state, action as AppAction<string>);
    case actionTypes.SHARED_GET_FORM_NAME_FAIL:
      return getFormNameFail(state, action as AppAction<string>);

    case actionTypes.SHARED_GET_BUSINESS_AUTO_ACORD_137s_LOOKUP_START:
      return getBusinessAutoAcord137sLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_BUSINESS_AUTO_ACORD_137s_LOOKUP_SUCCESS:
      return getBusinessAutoAcord137sLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_BUSINESS_AUTO_ACORD_137s_LOOKUP_FAIL:
      return getBusinessAutoAcord137sLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_PERSONAL_UMBRELLAS_LOOKUP_START:
      return getPersonalUmbrellasLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_PERSONAL_UMBRELLAS_LOOKUP_SUCCESS:
      return getPersonalUmbrellasLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_PERSONAL_UMBRELLAS_LOOKUP_FAIL:
      return getPersonalUmbrellasLookupFail(state, action as AppAction<DataLookup>);

    case actionTypes.SHARED_GET_COMMERCIAL_UMBRELLAS_LOOKUP_START:
      return getCommercialUmbrellasLookupStart(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_COMMERCIAL_UMBRELLAS_LOOKUP_SUCCESS:
      return getCommercialUmbrellasLookupSuccess(state, action as AppAction<DataLookup>);
    case actionTypes.SHARED_GET_COMMERCIAL_UMBRELLAS_LOOKUP_FAIL:
      return getCommercialUmbrellasLookupFail(state, action as AppAction<DataLookup>);

    default:
      return state;
  }
};

export default reducer;
